import React, {
  useRef,
  useState,
  forwardRef,
  ForwardedRef,
  useEffect,
} from "react";
import classNames from "classnames";
import styles from "./TextInput.module.scss";

export enum InputSize {
  DEFAULT = "default",
  SMALL = "small",
}

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  name: string;
  hint?: string;
  value: any;
  onChange: (...event: any[]) => void;
  onBlur: () => void;
  maxLength?: number;
  disableDecimal?: boolean;
  leftIcon?: React.ReactNode;
  decimalPlaces?: number;
  inputType?: "text" | "number";
  fontSize?: string;
  inputSize?: InputSize;
}

const isAndroid = /Android/i.test(navigator.userAgent);

const TextInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      hint,
      value,
      onChange,
      onBlur,
      maxLength = 200,
      type = "text",
      error,
      disableDecimal = false,
      leftIcon,
      decimalPlaces,
      inputType = "text",
      fontSize = "0.875rem",
      inputSize = InputSize.DEFAULT,
      ...rest
    },
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [focused, setFocused] = useState(false);

    const isNumber = inputType === "number";
    const isFilled = typeof value === "string" ? value.length > 0 : !!value;

    const isArabicDigit = (char: string) => /[\u0660-\u0669]/.test(char);

    const convertArabicToEnglishDigits = (input: string): string => {
      return input.replace(/[\u0660-\u0669]/g, (digit) =>
        String(digit.charCodeAt(0) - 0x0660)
      );
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value;

      if (isNumber) {
        newValue = convertArabicToEnglishDigits(newValue);
      }

      if (isAndroid) {
        // 🚫 Remove leading space
        newValue = newValue.replace(/^\s+/, "");

        if (isNumber) {
          // 🚫 Remove all except digits and dot
          newValue = newValue.replace(/[^0-9.]/g, "");

          // 🚫 Disallow dot at the start
          if (newValue.startsWith(".")) {
            newValue = "";
          }

          // 🚫 Disallow multiple dots
          const dotCount = (newValue.match(/\./g) || []).length;
          if (dotCount > 1) {
            const firstDotIndex = newValue.indexOf(".");
            newValue =
              newValue.slice(0, firstDotIndex + 1) +
              newValue.slice(firstDotIndex + 1).replace(/\./g, "");
          }

          // 🚫 Enforce decimalPlaces limit
          if (decimalPlaces !== undefined && newValue.includes(".")) {
            const [intPart, decimalPart] = newValue.split(".");
            newValue = intPart + "." + decimalPart.slice(0, decimalPlaces);
          }

          // 🚫 Remove dot if disableDecimal is true
          if (disableDecimal) {
            newValue = newValue.replace(/\./g, "");
          }
        }
      }

      onChange({
        ...e,
        target: {
          ...e.target,
          name,
          value: newValue,
        },
      });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isNumber && isArabicDigit(e.key)) {
        e.preventDefault(); // ⛔ prevent Arabic
        const englishDigit = convertArabicToEnglishDigits(e.key);

        const input = e.currentTarget;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const currentValue = input.value;

        const newValue =
          currentValue.substring(0, start) +
          englishDigit +
          currentValue.substring(end);

        onChange({
          ...e,
          target: {
            ...e.target,
            name,
            value: newValue,
          },
        });
        return;
      }

      const input = e.currentTarget;
      const isControl = [
        "Backspace",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
      ].includes(e.key);
      const isNumberChar = /^[0-9.]$/.test(e.key);

      const val = String(value || "");
      const selection = {
        start: input.selectionStart ?? 0,
        end: input.selectionEnd ?? 0,
      };

      const isFullSelect =
        selection.start === 0 && selection.end === val.length;
      if (e.key === " " && (val.length === 0 || isFullSelect)) {
        e.preventDefault();
        return;
      }

      if (!isNumber || isControl) return;

      if (!isNumberChar) {
        e.preventDefault();
        return;
      }

      const hasDot = val.includes(".");
      if (e.key === ".") {
        const isAtStart = selection.start === 0;
        if (disableDecimal || hasDot || isAtStart) {
          e.preventDefault();
          return;
        }
      }

      if (decimalPlaces !== undefined && hasDot) {
        const [_, decimals] = val.split(".");
        if (decimals?.length >= decimalPlaces && e.key !== ".") {
          e.preventDefault();
        }
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);

      let inputValue = String(e.target.value || "");

      // Trim trailing spaces on blur
      inputValue = inputValue.replace(/\s+$/, "");

      // Fix for numbers ending in a dot
      if (inputType === "number" && inputValue.endsWith(".")) {
        inputValue += "0";
      }

      // Update the value in React Hook Form
      onChange({
        ...e,
        target: {
          ...e.target,
          name,
          value: inputValue,
        },
      });

      onBlur?.();
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      let pasted = e.clipboardData.getData("Text");

      if (isNumber) {
        pasted = convertArabicToEnglishDigits(pasted);

        const numericSanitized = pasted.replace(/[^0-9.]/g, "");

        const dotIndex = numericSanitized.indexOf(".");
        if (dotIndex !== -1 && decimalPlaces !== undefined) {
          const decimals = numericSanitized.slice(dotIndex + 1);
          if (decimals.length > decimalPlaces) {
            e.preventDefault();
            return;
          }
        }

        let newValue = String(value || "");
        if (numericSanitized.startsWith(".")) {
          e.preventDefault();
          return;
        }
        newValue += numericSanitized;

        e.preventDefault();
        onChange({
          ...e,
          target: {
            ...e.target,
            name,
            value: newValue,
          },
        });
        return;
      }

      const trimmed = pasted.replace(/^\s+/, "");
      if (trimmed !== pasted) {
        e.preventDefault();
        const newValue = String(value || "") + trimmed;
        onChange({
          ...e,
          target: {
            ...e.target,
            name,
            value: newValue,
          },
        });
      }
    };

    useEffect(() => {
      const handleClick = (e: MouseEvent) => {
        if (wrapperRef.current?.contains(e.target as Node)) {
          (ref as React.RefObject<HTMLInputElement>)?.current?.focus?.();
        }
      };

      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }, [ref]);

    return (
      <>
        <div
          ref={wrapperRef}
          className={classNames(styles.container, styles[`${inputSize}`], {
            [styles.active]: focused,
            [styles.filled]: isFilled,
            [styles.error]: !!error,
            [styles.withPlaceholder]: !!rest?.placeholder,
          })}>
          {rest?.placeholder && (
            <label className={styles.floatingLabel}>{rest.placeholder}</label>
          )}
          {leftIcon && <div className={styles.leftIcon}>{leftIcon}</div>}
          <input
            ref={ref}
            name={name}
            value={value}
            className={classNames(styles.input)}
            onChange={(e) => {
              isAndroid ? handleChange(e) : onChange(e);
            }}
            onBlur={handleBlur}
            onFocus={() => setFocused(true)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            inputMode={inputType === "number" ? "decimal" : undefined}
            {...rest}
          />
        </div>
        {!error && hint && <p className={styles.hintText}>{hint}</p>}
        {error && (
          <p style={{ fontSize: fontSize }} className={styles.errorText}>
            {error}
          </p>
        )}
      </>
    );
  }
);

TextInput.displayName = "TextInput";
export default TextInput;
